import { useState, useEffect } from 'react'

import { Container, Input, Label, LabelGroup, RadioList, RadioWrapper } from './RadioGroup.styled'

export interface RadioOption {
  value: string
  label: string
  id: string
}

export interface RadioGroupProps {
  options: RadioOption[]
  value?: string
  defaultValue?: string
  name?: string
  onChange?: (value: string) => void
  label?: string
  required?: boolean
  disabled?: boolean
}

export const RadioGroup = ({
  options,
  label,
  value,
  defaultValue,
  required = false,
  disabled = false,
  name = 'radio-group',
  onChange,
}: RadioGroupProps): JSX.Element => {
  const [internalValue, setInternalValue] = useState<string | undefined>(defaultValue)
  const [touched, setTouched] = useState<boolean>(false)

  const labelId = `${name}-label`

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value)
    }
  }, [value])

  const handleChange = (newValue: string) => {
    setTouched(true)
    setInternalValue(newValue)
    onChange?.(newValue)
  }

  const handleBlur = () => {
    setTouched(true)
  }

  return (
    <Container>
      {label && <LabelGroup id={labelId}>{label}</LabelGroup>}
      <RadioList
        role="radiogroup"
        aria-required={required}
        aria-labelledby={label ? labelId : undefined}
        onBlur={handleBlur}
      >
        {options.map(({ value: optionValue, label, id }: RadioOption) => (
          <RadioWrapper key={id}>
            <Input
              type="radio"
              name={name}
              id={id}
              value={optionValue}
              checked={internalValue === optionValue}
              onChange={() => handleChange(optionValue)}
              required={required}
              aria-invalid={touched && required && !internalValue}
              disabled={disabled}
            />
            <Label htmlFor={id} disabled={disabled}>
              {label}
            </Label>
          </RadioWrapper>
        ))}
      </RadioList>
    </Container>
  )
}
